import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'
import { Calendar, Eye } from 'lucide-react'

import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Separator } from '@/shared/ui/separator'
import { Textarea } from '@/shared/ui/textarea'
import { RoomAssignPopover } from '@/widgets/RoomAssignPopover'
import { HotelReservation } from '@monorepo/functions/src/types/reservations'
import { AdditionalService } from '@monorepo/functions/src/types/services'

import { useReservationModal } from '../../components/ReservationModalContext'
import { SPECIAL_CONDITIONS } from '../model/constants/special-conditions'
import { PetInfo } from './room-details-modal'

type PetRoomDetailsProps = {
    reservationId: HotelReservation
    pet: PetInfo
    onPetChange: (petId: string, pet: Partial<PetInfo>) => void
    onRoomChange: (petId: string, room: string) => void
    onServiceChange?: (services: AdditionalService[]) => void
    reservations: HotelReservation[]
    onClose?: () => void
}

const sizeOptions = ['pequeño', 'mediano', 'grande'] as const

export const PetRoomDetails = ({
    reservationId: reservation,
    pet,
    onPetChange,
    onRoomChange,
    onServiceChange,
    reservations,
    onClose,
}: PetRoomDetailsProps) => {
    const [isRoomAssignPopoverOpen, setIsRoomAssignPopoverOpen] = useState(false)
    const { openReservationModal } = useReservationModal()
    const { t } = useTranslation()

    // Helper function to get pet index from pet ID
    const getPetIndex = () => {
        const petIdParts = pet.id.split('-')
        const reservationId = petIdParts[0]
        const petName = petIdParts[1]
        const reservation = reservations.find(r => r.id === reservationId)
        return reservation?.pets.findIndex(p => p.name === petName) ?? 0
    }

    // Helper function to check if a service exists for this pet
    const hasService = (serviceType: 'medication' | 'special_food') => {
        const petIndex = getPetIndex()
        return reservation.additionalServices.some(
            service => service.type === serviceType && service.petIndex === petIndex,
        )
    }

    // Helper function to toggle a service
    const toggleService = (serviceType: 'medication' | 'special_food') => {
        if (!onServiceChange) return

        const petIndex = getPetIndex()
        const currentServices = reservation.additionalServices
        const hasExistingService = hasService(serviceType)

        let updatedServices: AdditionalService[]

        if (hasExistingService) {
            // Remove the service
            updatedServices = currentServices.filter(
                service => !(service.type === serviceType && service.petIndex === petIndex),
            )
        } else {
            // Add the service
            const newService: AdditionalService =
                serviceType === 'medication'
                    ? {
                          type: 'medication',
                          petIndex,
                          comment: '',
                          frequency: 'single',
                          price: 0,
                      }
                    : {
                          type: 'special_food',
                          petIndex,
                          foodType: 'refrigerated',
                          price: 0,
                      }
            updatedServices = [...currentServices, newService]
        }

        onServiceChange(updatedServices)
    }

    return (
        <div className='space-y-6'>
            <div className='grid grid-cols-2 gap-6'>
                <div>
                    <label className='mb-1 block text-sm font-medium text-gray-700'>Raza</label>
                    <Input value={pet.breed} onChange={e => onPetChange(pet.id, { breed: e.target.value })} />
                </div>
                <div>
                    <label className='mb-1 block text-sm font-medium text-gray-700'>Tamaño</label>
                    <Select
                        value={pet.size}
                        onValueChange={(value: 'pequeño' | 'mediano' | 'grande') =>
                            onPetChange(pet.id, { size: value })
                        }
                    >
                        <SelectTrigger>
                            <SelectValue placeholder='Selecciona el tamaño' />
                        </SelectTrigger>
                        <SelectContent>
                            {sizeOptions.map(size => (
                                <SelectItem key={size} value={size}>
                                    {size.charAt(0).toUpperCase() + size.slice(1)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-2'>
                    <Calendar className='h-5 w-5 text-gray-400' />
                    <span className='text-sm text-gray-600'>
                        {format(new Date(pet.checkInDate), 'dd/MM/yyyy')} -{' '}
                        {format(new Date(pet.checkOutDate), 'dd/MM/yyyy')}
                    </span>
                </div>
                <div className='w-fit'>
                    <RoomAssignPopover
                        isOpen={isRoomAssignPopoverOpen}
                        setIsOpen={setIsRoomAssignPopoverOpen}
                        reservations={reservations}
                        selectedRoom={pet.roomNumber}
                        onRoomAssign={room => onRoomChange(pet.id, room)}
                    />
                </div>
            </div>
            <Separator />
            <div>
                <label className='mb-2 block text-sm font-medium text-gray-700'>Condiciones Especiales</label>
                <div className='flex flex-wrap gap-2'>
                    {SPECIAL_CONDITIONS.map(condition => (
                        <Badge
                            key={condition.type}
                            variant={
                                condition.type === 'medication' || condition.type === 'special_food'
                                    ? hasService(condition.type)
                                        ? 'default'
                                        : 'outline'
                                    : !pet.conditions || !pet.conditions.some(c => c.type === condition.type)
                                      ? 'outline'
                                      : 'default'
                            }
                            className='cursor-pointer px-2 py-1 text-sm'
                            onClick={() => {
                                if (condition.type === 'medication' || condition.type === 'special_food') {
                                    // Handle service-based conditions
                                    toggleService(condition.type)
                                } else {
                                    // Handle regular pet conditions
                                    console.log('pet.conditions', pet.conditions, condition)
                                    const newConditions = pet.conditions?.some(c => c.type === condition.type)
                                        ? pet.conditions?.filter(c => c.type !== condition.type)
                                        : [...(pet.conditions ?? []), condition]
                                    console.log('newConditions', newConditions)
                                    onPetChange(pet.id, {
                                        conditions: newConditions.filter(
                                            c => c.type !== 'medication' && c.type !== 'special_food',
                                        ),
                                    })
                                }
                            }}
                        >
                            {condition.symbol} {condition.label}
                        </Badge>
                    ))}
                </div>
            </div>
            <div>
                <label className='mb-1 block text-sm font-medium text-gray-700'>Observaciones</label>
                <Textarea
                    value={pet.observations || ''}
                    onChange={e => onPetChange(pet.id, { observations: e.target.value })}
                    rows={4}
                    placeholder='Añade observaciones sobre la mascota...'
                />
            </div>
            <div className='flex justify-end pt-4'>
                <Button
                    variant='outline'
                    onClick={() => {
                        openReservationModal(reservation)
                        onClose?.()
                    }}
                    className='flex items-center gap-2'
                >
                    <Eye className='h-4 w-4' />
                    {t('petRoomDetails.viewReservation', 'Ver reserva')}
                </Button>
            </div>
        </div>
    )
}
